import { authService } from 'modules/auth/services/authService';
import type {
  ComandoLinkedIn,
  ContaLinkedIn,
  ConversaPagina,
  ConversasFiltro,
  DesligarTudoResultado,
  LogLinkedIn,
  MensagemLinkedIn,
  SalvarMinhaContaRequest,
  SaudeLinkedIn,
  WorkerStatusLinkedIn
} from './tipos';

/**
 * Acesso à API do LinkedIn do vendedor e da gestão (`src/Rocky.Modules.CRM/Features/LinkedIn/LinkedInEndpoints.cs`).
 * As rotas do worker (`/worker/*`) não são da tela: exigem chave de API.
 *
 *   Minha conta  GET  /minha-conta                     (204 = ainda não conectou → null)
 *                PUT  /minha-conta                     (cria ou atualiza; pausar true/false)
 *   Caixa        GET  /conversas?contaId&naoLidas&inMail&convites&busca&pagina&tamanho
 *                GET  /conversas/{id}/mensagens?antesDe&take   (abrir zera as não lidas do dono)
 *                POST /conversas/{id}/responder        → 202 com o comando enfileirado
 *   Envio        GET  /comandos?contaId&status
 *                POST /comandos/{id}/aprovar           (texto editado opcional)
 *                POST /comandos/{id}/pular
 *   Gestão       GET  /contas · POST /contas/{id}/pausar · POST /contas/{id}/retomar · POST /desligar-tudo
 *                GET  /worker-status
 *   Saúde        GET  /saude?contaId · GET /log?contaId&tipo&limite · POST /varrer-agora?contaId (202; 409 pausada)
 *
 * 401 é da sessão e o `authService` cuida; aqui chegam 400/403/404/409 como `Error` com `status`.
 */
const BASE = '/api/crm/linkedin';

const qs = (params: Record<string, string | number | boolean | null | undefined>): string => {
  const e = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false);
  return e.length ? '?' + e.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&') : '';
};

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

/** Avisa os contadores do menu de que a fila ou a caixa mudou (aprovar, pular, abrir conversa). */
export const EVENTO_LINKEDIN_MUDOU = 'rocky:linkedin-mudou';
export const avisarLinkedInMudou = (): void => {
  try {
    window.dispatchEvent(new Event(EVENTO_LINKEDIN_MUDOU));
  } catch {
    /* fora do navegador: nada a avisar */
  }
};

export const linkedinService = {
  /** `null` quando o vendedor ainda não tem conexão (o GET responde 204). */
  minhaConta: () => authService.authFetch<ContaLinkedIn>(`${BASE}/minha-conta`),

  salvarMinhaConta: (req: SalvarMinhaContaRequest) => authService.authFetch<ContaLinkedIn>(`${BASE}/minha-conta`, json('PUT', req)),

  listarConversas: async (f: ConversasFiltro): Promise<ConversaPagina> =>
    (await authService.authFetch<ConversaPagina>(`${BASE}/conversas${qs({ ...f })}`)) ?? {
      itens: [],
      total: 0,
      pagina: f.pagina ?? 1,
      tamanho: f.tamanho ?? 30
    },

  /** Só o total do recorte (o contador do menu). */
  contarConversas: async (f: ConversasFiltro): Promise<number> =>
    (await authService.authFetch<ConversaPagina>(`${BASE}/conversas${qs({ ...f, pagina: 1, tamanho: 1 })}`))?.total ?? 0,

  /** Em ordem cronológica (a mais antiga primeiro). */
  mensagens: async (conversaId: number, antesDe?: string, take = 100): Promise<MensagemLinkedIn[]> =>
    (await authService.authFetch<MensagemLinkedIn[]>(`${BASE}/conversas/${conversaId}/mensagens${qs({ antesDe, take })}`)) ?? [],

  responder: (conversaId: number, texto: string, idempotenciaChave: string) =>
    authService.authFetch<ComandoLinkedIn>(`${BASE}/conversas/${conversaId}/responder`, json('POST', { texto, idempotenciaChave })),

  comandos: async (params: { contaId?: number; status?: string } = {}): Promise<ComandoLinkedIn[]> =>
    (await authService.authFetch<ComandoLinkedIn[]>(`${BASE}/comandos${qs(params)}`)) ?? [],

  /** `texto` nulo aprova o que já está. */
  aprovar: (comandoId: number, texto: string | null) =>
    authService.authFetch<ComandoLinkedIn>(`${BASE}/comandos/${comandoId}/aprovar`, json('POST', { texto })),

  pular: (comandoId: number) => authService.authFetch<ComandoLinkedIn>(`${BASE}/comandos/${comandoId}/pular`, { method: 'POST' }),

  listarContas: async (): Promise<ContaLinkedIn[]> => (await authService.authFetch<ContaLinkedIn[]>(`${BASE}/contas`)) ?? [],

  pausarConta: (contaId: number, motivo: string | null) =>
    authService.authFetch<ContaLinkedIn>(`${BASE}/contas/${contaId}/pausar`, json('POST', { motivo })),

  retomarConta: (contaId: number) => authService.authFetch<ContaLinkedIn>(`${BASE}/contas/${contaId}/retomar`, { method: 'POST' }),

  desligarTudo: () => authService.authFetch<DesligarTudoResultado>(`${BASE}/desligar-tudo`, { method: 'POST' }),

  /** Últimos 30 dias da sessão. Sem `contaId`, a do próprio usuário; com, exige ser dono ou ver_todas (404). */
  saude: (contaId?: number) => authService.authFetch<SaudeLinkedIn>(`${BASE}/saude${qs({ contaId })}`),

  /** Log recente (eventos e comandos), sem texto de mensagem. */
  log: (params: { contaId?: number; tipo?: string; limite?: number } = {}) =>
    authService.authFetch<LogLinkedIn>(`${BASE}/log${qs(params)}`),

  /** 202 com o comando VARRER_AGORA; 409 com a conta pausada ou desligada. */
  varrerAgora: (contaId?: number) => authService.authFetch<ComandoLinkedIn>(`${BASE}/varrer-agora${qs({ contaId })}`, { method: 'POST' }),

  /** Gestão (`crm.linkedin.ver_todas`): versão, máquina e último batimento do worker. */
  workerStatus: () => authService.authFetch<WorkerStatusLinkedIn>(`${BASE}/worker-status`)
};

/** Status HTTP do erro do `authFetch` (400/403/404/409…), quando houver. */
export const statusDoErro = (e: unknown): number | undefined => (e as { status?: number } | null)?.status;

export const mensagemDoErro = (e: unknown, padrao: string): string => (e instanceof Error && e.message ? e.message : padrao);
